import { environment } from "../../config/environment";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button } from "../ui/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/Dialog";
import { GitCompare, Loader2 } from "lucide-react";
import { customFetch } from "../../utils/Auth";
import SplitDiffView from "./SplitDiffView";
import { getTemplateDisplayTime } from "./timeUtils";

const VersionMeta = ({ version, formatTimestamp }) => {
  if (!version) return null;
  const displayTime = getTemplateDisplayTime(version);
  return (
    <div className="text-sm text-gray-500 mt-0.5">
      {version.is_default === true && (
        <span className="font-medium text-gray-600">Default</span>
      )}
      {version.is_default === true && version.username && (
        <span> &middot; </span>
      )}
      {version.username && (
        <span className="font-medium text-gray-600">{version.username}</span>
      )}
      {version.username && displayTime && <span> &middot; </span>}
      {displayTime && formatTimestamp(displayTime)}
    </div>
  );
};

// Side-by-side comparison of two versions of the same template. `versionA` is
// the older (left) version and `versionB` the newer (right) one.
const TemplateCompareModal = ({
  open,
  onOpenChange,
  templateName,
  versionA,
  versionB,
  formatTimestamp,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Lets the user flip which version sits on the left vs. right. Defaults to
  // versionA (older) on the left, versionB (newer) on the right.
  const [swapped, setSwapped] = useState(false);

  const textCacheRef = useRef({});
  const [cacheVersion, setCacheVersion] = useState(0);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Key by name + version so this works whether the two sides are versions of
  // the same template or two entirely different templates.
  const cacheKey = useCallback(
    (tpl) => (tpl ? `${tpl.name}::${tpl.version}` : ""),
    []
  );

  const fetchVersionText = useCallback(
    async (tpl) => {
      if (!tpl || tpl.version === undefined || tpl.version === null) {
        return undefined;
      }
      const key = cacheKey(tpl);
      if (textCacheRef.current[key] !== undefined) {
        return textCacheRef.current[key];
      }
      const url = `${environment.apiUrl}/templates/get-template?name=${encodeURIComponent(
        tpl.name
      )}&version=${encodeURIComponent(tpl.version)}`;
      const response = await customFetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const text = data.text || "";
      textCacheRef.current[key] = text;
      if (isMountedRef.current) setCacheVersion((v) => v + 1);
      return text;
    },
    [cacheKey]
  );

  useEffect(() => {
    if (!open || !versionA || !versionB) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setError(null);
        setLoading(true);
        await Promise.all([
          fetchVersionText(versionA),
          fetchVersionText(versionB),
        ]);
      } catch (err) {
        console.error("Error loading template versions for compare:", err);
        if (!cancelled && isMountedRef.current) {
          setError("Failed to load template content");
        }
      } finally {
        if (!cancelled && isMountedRef.current) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, versionA, versionB, fetchVersionText]);

  // Reset the swap whenever a fresh pair is opened.
  useEffect(() => {
    setSwapped(false);
  }, [versionA, versionB, open]);

  // The versions as actually displayed (left/right), honoring the swap toggle.
  const leftVersion = swapped ? versionB : versionA;
  const rightVersion = swapped ? versionA : versionB;

  // Both sides' text, in display order, once the async fetches have landed.
  // `null` while either side is still outstanding.
  const diffText = useMemo(() => {
    if (!leftVersion || !rightVersion) return null;
    const left = textCacheRef.current[cacheKey(leftVersion)];
    const right = textCacheRef.current[cacheKey(rightVersion)];
    if (left === undefined || right === undefined) return null;
    return { left, right };
    // cacheVersion forces recompute once async text lands in the cache
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftVersion, rightVersion, cacheVersion, cacheKey]);

  // Two sides may be versions of one template, or two distinct templates.
  const sameTemplate =
    !!versionA && !!versionB && versionA.name === versionB.name;

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ktx-purple" />
          <p className="ml-2 text-gray-600">Loading...</p>
        </div>
      );
    }
    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
        </div>
      );
    }
    if (!diffText) return null;

    return (
      <SplitDiffView
        open={open}
        leftText={diffText.left}
        rightText={diffText.right}
        onToggleSwap={() => setSwapped((s) => !s)}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[1500px] h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-3xl font-semibold text-gray-900 flex items-center gap-3 leading-none">
            <GitCompare className="h-8 w-8 shrink-0" />
            <span className="leading-none">
              {sameTemplate ? "Compare Versions" : "Compare Templates"}
            </span>
          </DialogTitle>
          {sameTemplate && (
            <DialogDescription className="text-xl text-gray-700 mt-2">
              {versionA?.name || templateName}
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Column headers. Left is the diff base (red), right the target
            (green); the swap button flips which version sits on each side. */}
        <div className="grid grid-cols-2 gap-0 border-b border-gray-200 pb-2">
          <div className="pr-4">
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm bg-red-200 border border-red-300" />
              <span className="text-lg font-semibold text-gray-900">
                {sameTemplate
                  ? leftVersion?.version || "v1.0"
                  : leftVersion?.name}
              </span>
              {!sameTemplate && (
                <span className="text-sm text-gray-500">
                  {leftVersion?.version || "v1.0"}
                </span>
              )}
            </div>
            <VersionMeta
              version={leftVersion}
              formatTimestamp={formatTimestamp}
            />
          </div>
          <div className="pl-4">
            <div className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 rounded-sm bg-green-200 border border-green-300" />
              <span className="text-lg font-semibold text-gray-900">
                {sameTemplate
                  ? rightVersion?.version || "v1.0"
                  : rightVersion?.name}
              </span>
              {!sameTemplate && (
                <span className="text-sm text-gray-500">
                  {rightVersion?.version || "v1.0"}
                </span>
              )}
            </div>
            <VersionMeta
              version={rightVersion}
              formatTimestamp={formatTimestamp}
            />
          </div>
        </div>

        {renderBody()}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="text-lg px-6 py-3 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateCompareModal;
